import { useCallback } from "react"
import { apiFetch } from "../../../utils/apiFetch"

export default function useAgendaFinance({
  API_BASE,
  clinicId,
  date,
  selectedAppointment,
  paymentForm,
  receivableForm,
  setRegisteringPayment,
  setCreatingReceivable,
  setSelectedAppointment,
  loadHeldSlots,
  loadAgenda,
  loadFinancialSummary,
  loadPatientSnapshot,
  isReadOnly,
  readOnlyReason
}) {
  const resolveMutationClinicId = useCallback(() => {
    if (clinicId && clinicId !== "ALL") return clinicId
    if (selectedAppointment?.clinicId) return selectedAppointment.clinicId
    if (selectedAppointment?.extendedProps?.clinicId) {
      return selectedAppointment.extendedProps.clinicId
    }
    return ""
  }, [clinicId, selectedAppointment])

  const refreshAgendaView = useCallback(
    async (targetClinicId = resolveMutationClinicId(), targetDate = date) => {
      if (!targetClinicId || !targetDate) return []

      const freshHeld = typeof loadHeldSlots === "function"
        ? await loadHeldSlots(targetClinicId, targetDate)
        : null

      if (typeof loadAgenda === "function") {
        return loadAgenda(targetClinicId, targetDate, freshHeld)
      }

      return []
    },
    [date, loadHeldSlots, loadAgenda, resolveMutationClinicId]
  )

  const applyFinancialStateToSelectedAppointment = useCallback((summary) => {
    if (typeof setSelectedAppointment !== "function" || !selectedAppointment?.id) return

    const producedAmount = pickNumber(summary, ["produced", "producedAmount", "totalProduced"])
    const paidAmount = pickNumber(summary, ["paid", "paidAmount", "charged", "totalPaid"])
    const pendingAmount = pickNumber(summary, ["pendingReceivables", "pending", "pendingAmount", "balance"])
    const financialStatus = resolveFinancialStatus({ producedAmount, paidAmount, pendingAmount })

    setSelectedAppointment((prev) => {
      if (!prev) return prev

      return {
        ...prev,
        producedAmount,
        paidAmount,
        pendingAmount,
        financialStatus,
        lastPaymentRegisteredAt: new Date().toISOString(),
        extendedProps: {
          ...(prev.extendedProps || {}),
          producedAmount,
          paidAmount,
          pendingAmount,
          financialStatus,
        },
      }
    })
  }, [selectedAppointment, setSelectedAppointment])

  const refreshFinancialContext = useCallback(async () => {
    const mutationClinicId = resolveMutationClinicId()
    let summary = null

    if (selectedAppointment?.id) {
      summary = await loadFinancialSummary(selectedAppointment.id, mutationClinicId)
      applyFinancialStateToSelectedAppointment(summary)
    }

    if (selectedAppointment?.patientId) {
      await loadPatientSnapshot(selectedAppointment.patientId, mutationClinicId)
    }

    await refreshAgendaView(mutationClinicId, date)

    return summary
  }, [
    selectedAppointment,
    date,
    loadFinancialSummary,
    loadPatientSnapshot,
    resolveMutationClinicId,
    refreshAgendaView,
    applyFinancialStateToSelectedAppointment
  ])

  const registerPaymentForSelectedAppointment = useCallback(async () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return false
    }

    if (!selectedAppointment?.id) return false

    const mutationClinicId = resolveMutationClinicId()

    if (!mutationClinicId) {
      alert(
        clinicId === "ALL"
          ? "Vista consolidada activa. No pude identificar la clínica real de esta cita."
          : "No se pudo identificar la clínica de la cita."
      )
      return false
    }

    const amount = parseMoneyInput(paymentForm.amount)

    if (!Number.isFinite(amount) || amount <= 0) {
      alert("El monto del pago debe ser mayor a 0")
      return false
    }

    try {
      setRegisteringPayment(true)

      const res = await apiFetch(`${API_BASE}/appointments/register-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          appointmentId: selectedAppointment.id,
          amount,
          method: paymentForm.method,
          notes: paymentForm.notes
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo registrar el pago")
        return false
      }

      if (data?.summary) {
        applyFinancialStateToSelectedAppointment(data.summary)
      }

      await refreshFinancialContext()
      alert(data?.message || "Pago registrado correctamente. La cita ya fue actualizada.")
      return true
    } catch (error) {
      alert(error?.message || "Error al registrar pago")
      return false
    } finally {
      setRegisteringPayment(false)
    }
  }, [
    isReadOnly,
    readOnlyReason,
    API_BASE,
    clinicId,
    selectedAppointment,
    paymentForm,
    setRegisteringPayment,
    refreshFinancialContext,
    resolveMutationClinicId,
    applyFinancialStateToSelectedAppointment
  ])

  const cancelPaymentForSelectedAppointment = useCallback(async (paymentId) => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return false
    }

    if (!selectedAppointment?.id || !paymentId) return false

    try {
      setRegisteringPayment(true)

      const res = await apiFetch(`${API_BASE}/appointments/cancel-payment-from-appointment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ paymentId, appointmentId: selectedAppointment.id })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo cancelar el pago")
        return false
      }

      await refreshFinancialContext()
      alert(data?.message || "Pago cancelado correctamente")
      return true
    } catch (error) {
      alert(error?.message || "Error al cancelar el pago")
      return false
    } finally {
      setRegisteringPayment(false)
    }
  }, [
    isReadOnly,
    readOnlyReason,
    API_BASE,
    selectedAppointment,
    setRegisteringPayment,
    refreshFinancialContext
  ])

  const createReceivableForSelectedAppointment = useCallback(async () => {
    if (isReadOnly) {
      alert(readOnlyReason || "Modo solo lectura activo")
      return false
    }

    if (!selectedAppointment?.id) return false

    const totalAmount = parseMoneyInput(receivableForm.totalAmount)

    if (!Number.isFinite(totalAmount) || totalAmount <= 0) {
      alert("El monto de la cuenta por cobrar debe ser mayor a 0")
      return false
    }

    try {
      setCreatingReceivable(true)

      const res = await apiFetch(`${API_BASE}/appointments/create-receivable`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          appointmentId: selectedAppointment.id,
          totalAmount
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo generar la cuenta por cobrar")
        return false
      }

      await refreshFinancialContext()
      alert(data?.message || "Cuenta por cobrar creada correctamente")
      return true
    } catch (error) {
      alert(error?.message || "Error al crear la cuenta por cobrar")
      return false
    } finally {
      setCreatingReceivable(false)
    }
  }, [
    isReadOnly,
    readOnlyReason,
    API_BASE,
    selectedAppointment,
    receivableForm,
    setCreatingReceivable,
    refreshFinancialContext
  ])

  return {
    registerPaymentForSelectedAppointment,
    createReceivableForSelectedAppointment,
    cancelPaymentForSelectedAppointment
  }
}

function parseMoneyInput(value) {
  const normalized = String(value || "").replace(/,/g, ".").replace(/[^0-9.]/g, "")
  const firstDot = normalized.indexOf(".")
  const safe = firstDot === -1
    ? normalized
    : normalized.slice(0, firstDot + 1) + normalized.slice(firstDot + 1).replace(/\./g, "")
  const parsed = Number(safe)
  return Number.isFinite(parsed) ? parsed : 0
}

function pickNumber(source, keys) {
  if (!source) return 0

  for (const key of keys) {
    const value = source?.[key]
    if (typeof value === "number") return value
    if (typeof value === "string" && value !== "" && !Number.isNaN(Number(value))) {
      return Number(value)
    }
  }

  return 0
}

function resolveFinancialStatus({ paidAmount, pendingAmount }) {
  if (pendingAmount > 0 && paidAmount > 0) return "PARTIAL"
  if (pendingAmount > 0) return "DEBT"
  if (paidAmount > 0) return "PAID"
  return "NONE"
}
